<template>
  <NuxtLayout name="auth">
    <div class="flex justify-center items-center p-4 bg-grid-pattern">
      <div class="max-w-xl w-full">
        <div class="text-center mb-4">
          <i
            :class="errorIcon"
            class="text-5xl mb-3"
            :style="{ color: errorColor }"
          />
          <h1 class="videophreak text-2xl mb-2">
            {{ errorTitle }}
          </h1>
          <div class="w-24 h-1 bg-primary-500 mx-auto mb-3" />
        </div>

        <div class="mb-4 terminal-text">
          <p class="mb-2 text-center">
            {{ errorMessage }}
          </p>
          <p
            v-if="props.error?.statusCode"
            class="text-sm text-surface-600 dark:text-surface-400 text-center"
          >
            Error code: {{ props.error.statusCode }}
            {{ props.error }}
          </p>
        </div>

        <div class="flex mt-6 justify-center gap-4">
          <Button
            label="Go Back"
            icon="pi pi-arrow-left"
            class="p-button-outlined"
            @click="goBack"
          />
          <Button
            label="Return Home"
            icon="pi pi-home"
            class="tech-button"
            @click="navigateTo('/')"
          />
        </div>
      </div>
    </div>
  </NuxtLayout>
</template>

<script setup lang="ts">
import type { NuxtError } from 'nuxt/app'

const props = defineProps({
  error: Object as () => NuxtError,
})

// Generate appropriate error title and message based on status code
const errorTitle = computed(() => {
  switch (props.error?.statusCode) {
    case 404: return 'PAGE NOT FOUND'
    case 403: return 'ACCESS DENIED'
    case 500: return 'SERVER ERROR'
    default: return 'ERROR ENCOUNTERED'
  }
})

const errorMessage = computed(() => {
  switch (props.error?.statusCode) {
    case 404:
      return 'The page you are looking for does not exist or has been moved.'
    case 403:
      return 'You do not have permission to access this resource.'
    case 500:
      return 'An unexpected server error occurred. Our team has been notified.'
    default:
      return props.error?.message || 'An unexpected error has occurred.'
  }
})

const errorIcon = computed(() => {
  switch (props.error?.statusCode) {
    case 404: return 'pi pi-exclamation-circle'
    case 403: return 'pi pi-lock'
    case 500: return 'pi pi-server'
    default: return 'pi pi-exclamation-triangle'
  }
})

const errorColor = computed(() => {
  switch (props.error?.statusCode) {
    case 404: return 'var(--p-blue-500)'
    case 403: return 'var(--p-red-500)'
    case 500: return 'var(--p-orange-500)'
    default: return 'var(--p-yellow-500)'
  }
})

// Function to go back to previous page
const goBack = () => {
  if (window.history.length > 1) {
    window.history.back()
  }
  else {
    navigateTo('/')
  }
}
</script>

<style scoped>
.tech-panel {
  /* Light mode styling */
  background: var(--surface-50);
  color: var(--text-color);
  border: 1px solid rgba(57, 155, 20, 0.3);
  box-shadow: 0 0 0 1px rgba(57, 155, 20, 0.2),
              inset 0 0 0 1px rgba(57, 155, 20, 0.1),
              0 5px 15px rgba(0, 0, 0, 0.1);
  padding: 2rem;
}

/* Dark mode styling */
:deep(.dark) .tech-panel,
.dark .tech-panel {
  background: var(--surface-900);
  border: 1px solid rgba(57, 255, 20, 0.3);
  box-shadow: 0 0 0 1px rgba(57, 255, 20, 0.2),
              inset 0 0 0 1px rgba(57, 255, 20, 0.1),
              0 5px 15px rgba(0, 0, 0, 0.3);
}

.tech-button {
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

.tech-button:hover {
  box-shadow: 0 0 10px rgba(57, 255, 20, 0.5);
  transform: translateY(-2px);
}
</style>
